// src/encounter/encounter-search-drawer.tsx
//
// THE LEFT DRAWER that hosts the encounter search rail. Two flows share it (Andrew's screenshots,
// 2026-08-08) — they differ only in title, footer and which favourites tabs show:
//
//   "Add Order"      opened from the cluster icon on EVERY encounter tab except Wrap-up. Favourites
//                    header carries three tabs (FAVOURITE DIAGNOSIS / ICD-10 / Bundles). No footer:
//                    a pick applies straight to the encounter.
//   "Create Bundle"  opened from the LAST cluster icon. ICD-10 only (you do not pick a bundle while
//                    authoring one). Save / Cancel pinned at the bottom, and picks accumulate in a
//                    SELECTION BUFFER rather than landing on the canvas.
//
// Flutter opens both through the same `openDrawer(isBundle:)` seam
// (encounter_mobile_screen.dart:180-215) — one host, one flag — which is why this is one component
// and not two. The header there is literally `bloc!.isBundle ? "Create Bundle" : "Add Order"`.
//
// This owns NO search behaviour: the rail (`ApSearchRailContainer` over the DS `ApSearchRail`) keeps its tool strip, query, favourites/
// global and every category hook. The drawer is chrome plus the footer contract.
//
// SHELL = the DS `Drawer` atom, side="left" — this is the OVERLAY variant ("over the rail", per this
// file's own header), not the static-aside shape the Drawer's own doc comment calls out as
// unserved. `showCloseButton={false}` because the close affordance sits LEADING the title (✕ on the
// left of a centred title, as drawn) rather than the DS default's trailing corner button — rendered
// through `headerLeading` with the same `Close <title>` accessible name the existing test pins, so
// the DS default `aria-label="Close"` close button is unused here.
import { type ReactNode } from 'react';
import { Drawer } from '@scripsteam/scrips-signal-ds';
import { ApSearchRailContainer, type ApSearchRailContainerProps } from './ap-search-rail';

export interface EncounterSearchDrawerProps extends ApSearchRailContainerProps {
  open: boolean;
  /** Header text — "Add Order" or "Create Bundle" in the live app. */
  title: string;
  onClose: () => void;
  /**
   * Present → the drawer is in SELECTION mode and pins a Save/Cancel footer (Create Bundle).
   * Absent → picks apply immediately and there is no footer (Add Order).
   */
  onSave?: () => void;
  /** Disables Save — Flutter refuses an empty selection ("Please select item to add"). */
  canSave?: boolean;
  /** Rendered above the rail — the Create-Bundle flow shows what has been picked so far. */
  children?: ReactNode;
}

export function EncounterSearchDrawer({
  open,
  title,
  onClose,
  onSave,
  canSave = false,
  children,
  ...railProps
}: EncounterSearchDrawerProps) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="left"
      width={320}
      showCloseButton={false}
      title={title}
      headerLeading={
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${title}`}
          className="cursor-pointer border-0 bg-transparent p-0 text-size-base leading-none text-text-muted"
        >
          ✕
        </button>
      }
      footer={
        onSave && (
          <>
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              // `--color-text-inverse` is the DS ink on brand; the `--color-text-on-brand` this used
              // to read is not a DS token, so the label inherited the drawer's dark ink.
              className="h-control-pill cursor-pointer rounded-full border-0 bg-brand-primary px-4.5 text-size-sm font-semibold text-text-inverse disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-control-pill cursor-pointer rounded-full border border-solid border-border-default bg-transparent px-4.5 text-size-sm text-text-secondary"
            >
              Cancel
            </button>
          </>
        )
      }
    >
      {/*
        CONDITIONAL MOUNT, DELIBERATELY — `ApSearchRailContainer` only mounts here, while the DS Drawer is
        open. Do not "optimise" this into a permanently-mounted rail.

        Unmounting is load-bearing, not incidental (PROD-1972). `ApSearchRailContainer` seeds its category
        and query from useState INITIALISERS, which run once per mount. That is only correct
        because every open is a fresh mount. Keep the rail mounted across opens and the seed
        silently goes stale: the ambient accept path would pre-fill a search with the PREVIOUS
        proposal's text, and a clinician confirming a diagnosis against real terminology would be
        shown the wrong query with no indication anything was wrong.

        If this ever must stay mounted (animation, focus retention), the rail needs an explicit
        reset — a `key` on the seed, or effect-based sync — BEFORE this changes.
      */}
      {open && (
        <>
          {children}
          <ApSearchRailContainer {...railProps} />
        </>
      )}
    </Drawer>
  );
}
